import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/navigation/Navbar";
import MainTitle from "../components/general/MainTitle";
import { useEffect, useMemo, useState } from "react";
import { HoursForm } from "../components/activities/formCards/hoursForm/HoursForm";
import { ChannelsForm } from "../components/activities/formCards/ChannelsForm";
import { PricesForm } from "../components/activities/formCards/pricesForm/PricesForm";
import { InformationsForm } from "../components/activities/formCards/InformationsForm";
import { useQuery } from "react-query";
import { Activity, getActivities } from "../backend/api/activities";
import { ActivityTypeEnum } from "../helpers/constants";
import {
  hasActivitySelectedPrice,
  hasActivitySelectedTimeslot,
  isArrayEmpty,
} from "helpers/helpers";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ActivitiesHeaderCard from "components/activities/activitiesHeaderCard/ActivitiesHeaderCard";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import SaveFormModal from "components/tabs/widget/saveFormModal/SaveFormModal";
import { useNavigationBlocker } from "components/tabs/widget/useNavigationBlocker";
import { ACTIVITY_SAVE_ORDER } from "constants/activityConstants";

export interface ActivityForm extends Omit<Activity, "id"> {
  id?: number;
}
const selectedActivityIdKey = "selectedActivityId";

export default function Activities() {
  const { t } = useTranslation("activities");
  const navigate = useNavigate();

  const [activities, setActivities] = useState<ActivityForm[]>([]);
  const [activity, setActivity] = useState<ActivityForm>();
  const [newActivityId, setNewActivityId] = useState("");

  const activityId = (activity as Activity)?.id || null;

  const [isInformationsLockerOpen, setIsInformationsLockerOpen] =
    useState(false);
  const [isHoursLockerOpen, setIsHoursLockerOpen] = useState(false);
  const [isPricesLockerOpen, setIsPricesLockerOpen] = useState(false);
  const [isChannelsLockerOpen, setIsChannelsLockerOpen] = useState(false);
  const [activateActivityPopupOpen, setActivateActivityPopupOpen] =
    useState(false);
  const [isSaveFormModalOpen, setIsSaveFormModalOpen] = useState(false);
  const initialSaveDataChoices = {
    save: false,
    quit: false,
    edit: false,
  };
  const [saveDataChoice, setSaveDataChoice] = useState(initialSaveDataChoices);
  const { navigationBlocker } = useNavigationBlocker();
  const dispatch = useDispatch();

  const { isActivitySaved, saveActivityInProgress } = useSelector(
    (state: RootState) => state.activity,
  );

  function getIdFromUrl(url: string) {
    const match = url.match(/\/id\/(\d+)/);
    return match ? match[1] : null;
  }

  const { isLoading, refetch } = useQuery({
    queryKey: ["getActivitiesWithDisabled"],
    queryFn: () => getActivities(true),
    // staleTime: 60000,
    refetchOnWindowFocus: false,
    onSuccess(data) {
      setActivities(data);
      // Restore last selected activity from localStorage
      // will go back to the last activity when updated, if no activity with id selected
      if (!isArrayEmpty(data) && !activity?.id) {
        const activeActivities = data.filter(
          (activity) => !activity.informations.isDeleted,
        );
        const savedActivityId = localStorage.getItem(selectedActivityIdKey);
        const activityId =
          getIdFromUrl(window.location.pathname) ?? savedActivityId;
        let selectedActivityId = undefined;
        if (!isArrayEmpty(activeActivities)) {
          const restoredActivity = activeActivities.find(
            (activity) => activity.id === Number(activityId),
          );
          selectedActivityId = restoredActivity ?? activeActivities.at(-1);
        }

        setActivity(selectedActivityId);
      } else {
        const activity = data.find((item) => item.id === activityId);
        setActivity(activity);
      }
    },
  });

  useEffect(() => {
    if (!activity?.id) setIsInformationsLockerOpen(true);
    else {
      setIsInformationsLockerOpen(false);
      localStorage.setItem(selectedActivityIdKey, String(activity.id));
    }
    setIsHoursLockerOpen(false);
    setIsPricesLockerOpen(false);
    setIsChannelsLockerOpen(false);
  }, [activities, activity]);

  useEffect(() => {
    if (navigationBlocker.state === "blocked" && !isActivitySaved) {
      setIsSaveFormModalOpen(true);
    }
  }, [navigationBlocker, isActivitySaved]);

  useEffect(() => {
    if (navigationBlocker.state === "blocked") {
      const { quit, save, edit } = saveDataChoice;
      if (quit) {
        navigationBlocker.proceed();
      } else if (save && !isActivitySaved) {
        dispatch({ type: ACTIVITY_SAVE_ORDER, payload: true });
      } else if (edit) {
        navigationBlocker.reset();
      }
      if (isActivitySaved && saveActivityInProgress) {
        dispatch({ type: ACTIVITY_SAVE_ORDER, payload: false });
        navigationBlocker.proceed();
        navigationBlocker.reset();
      }
    }
  }, [
    saveDataChoice,
    navigationBlocker,
    isActivitySaved,
    dispatch,
    saveActivityInProgress,
  ]);

  useEffect(() => {
    if (navigationBlocker.state === "unblocked") {
      if (isActivitySaved && newActivityId && !activityId) {
        navigate(`/activities/id/${newActivityId}`);
        setNewActivityId("");
        refetch();
      }
      setSaveDataChoice(initialSaveDataChoices);
    }
  }, [navigationBlocker, newActivityId, isActivitySaved, activityId]);

  const handleActivateActivitySwitchChange = async () => {
    if (activity) {
      if (!activity?.informations.isEnabled) {
        if (
          !(
            hasActivitySelectedPrice(activity) &&
            hasActivitySelectedTimeslot(activity)
          )
        )
          return setActivateActivityPopupOpen(true);
      }
      await setActivity({
        ...activity,
        informations: {
          ...activity?.informations,
          isEnabled: !activity?.informations.isEnabled,
        },
      });
      setIsChannelsLockerOpen(true);
    }
  };
  const closeActivateActivityPopup = () => {
    setActivateActivityPopupOpen(false);
  };
  const priceTierMaxCapacity = useMemo(() => {
    if (activity) {
      return activity.informations.bookingSettings.type ===
        ActivityTypeEnum.GROUP
        ? activity.informations.bookingSettings.maxUnitsPerGroup
        : activity.informations.bookingSettings.maxCapacity;
    } else {
      return 0;
    }
  }, [activity]);

  return (
    <Page>
      <Navbar />
      <Container>
        <MainTitle title={t("myActivitiesTitle")} />
        {!isLoading ? (
          <>
            <ActivitiesHeaderCard
              activity={activity}
              setActivity={setActivity}
              setActivities={setActivities}
              activities={activities}
            />
            {activity ? (
              <ActivityContent>
                <ActivityColumn>
                  <InformationsForm
                    inputData={activity.informations}
                    activityId={activityId}
                    isLockerOpen={isInformationsLockerOpen}
                    setIsLockerOpen={setIsInformationsLockerOpen}
                    navigate={navigate}
                    t={t}
                    setNewActivityId={setNewActivityId}
                  />
                </ActivityColumn>
                <ActivityColumn>
                  <HoursForm
                    inputData={activity.hours}
                    activityId={activityId}
                    isLockerOpen={isHoursLockerOpen}
                    setIsLockerOpen={setIsHoursLockerOpen}
                    navigate={navigate}
                    t={t}
                  />
                  <PricesForm
                    inputData={{ prices: activity.prices }}
                    activityId={activityId}
                    isLockerOpen={isPricesLockerOpen}
                    setIsLockerOpen={setIsPricesLockerOpen}
                    navigate={navigate}
                    t={t}
                    isGroupActivity={
                      activity.informations.bookingSettings.type ===
                      ActivityTypeEnum.GROUP
                    }
                    priceTierMaxCapacity={priceTierMaxCapacity}
                  />
                  <ChannelsForm
                    inputData={activity.channels}
                    activityId={activityId}
                    isLockerOpen={isChannelsLockerOpen}
                    setIsLockerOpen={setIsChannelsLockerOpen}
                    navigate={navigate}
                    t={t}
                    handleActivateActivitySwitchChange={
                      handleActivateActivitySwitchChange
                    }
                    activityInformations={activity.informations}
                  />
                  <Dialog
                    open={activateActivityPopupOpen}
                    onClose={closeActivateActivityPopup}
                  >
                    <DialogTitle
                      style={{
                        fontWeight: "700",
                        color: "#2CA58D",
                        fontSize: "24px",
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      {t("activateActivityWarningTitle")}
                    </DialogTitle>
                    <DialogContent>
                      {t("activateActivityWarningContent")}
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={closeActivateActivityPopup}
                        color="error"
                        variant="contained"
                      >
                        {t("close")}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </ActivityColumn>
              </ActivityContent>
            ) : null}
          </>
        ) : null}
      </Container>
      <SaveFormModal
        isSaveFormModalOpen={isSaveFormModalOpen}
        setIsSaveFormModalOpen={setIsSaveFormModalOpen}
        setSaveDataChoice={setSaveDataChoice}
      />
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  height: 100%;
  position: relative;
`;

const Container = styled.div`
  position: relative;
  margin: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  gap: 20px;
`;

const ActivityContent = styled.div`
  display: flex;
  gap: 15px;
  width: 100%;
`;

const ActivityColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;
